import React, { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { useDeepCompareMemo, useMountedEffect } from '@clatter/platform';
import { useShareDialog } from '../../hooks';
import { aclRolesMap, ServiceTypes } from '../../constants';
import { baseApi } from '../../services/baseApi';
import { FolderManagerItemTypes } from '../../interfaces';
import { useUpdateProjectMutation } from '../../services';
import { hasAccessByRole } from '../../helpers';

import { ProjectsLeftRail } from './components/ProjectsLeftRail';
import { FolderManagerShareDialog } from '../../components/FolderManager/Components/FolderManagerShareDialog';
import { NgEditablePageTitle, NgGridLayout, NgShareButton } from '../../components';
import { ProjectWorkbookUploadEventBus } from '../../events/project-workbook-upload';
import { authSelectors } from '../../store';

interface ProjectsLayoutProps {
  children: ReactNode;
  customContent?: ReactNode | null | undefined;
  projectSideNavItems: ProjectSideNavItem[];
  grants?: UserGrants;
  isLoading?: boolean;
  projectData: any;
}

const StyledHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;

  > *:first-child {
    flex: 0.6;
  }

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

export const ProjectsLayout: React.FC<ProjectsLayoutProps> = ({
  children,
  isLoading: isParentLoading,
  projectSideNavItems,
  grants,
  projectData,
}) => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const isAdmin = useSelector(authSelectors.isAdmin);

  const isMicrositeOnly = useMemo(() => {
    const docs = projectData?.Documents ?? [];
    if (docs.length === 0) return false;
    return docs.every((d: any) => (d?.DocumentTemplate?.AllowedOutputTypes ?? []).includes('html'));
  }, [projectData]);

  const isPlacematGenerator = projectData?.ProjectTemplate?.WorkspaceType === 'placemat-generator';

  const { shareDialogState, openShareDialog, closeShareDialog } = useShareDialog();

  const [updateProject, { isLoading: isUpdateProjectLoading }] = useUpdateProjectMutation();

  const isLoading = isParentLoading || isUpdateProjectLoading;

  //region METHODS
  const handleShare = () => {
    const workspaceData: Partial<FolderManagerItem> = {
      type: FolderManagerItemTypes.WORKSPACE,
      id: projectData.ID,
      displayName: projectData.DisplayName,
      name: projectData.DisplayName,
    };

    openShareDialog([workspaceData as FolderManagerItem]);
  };

  const revalidateProjectState = useCallback(async () => {
    await dispatch(baseApi.util.invalidateTags([{ type: ServiceTypes.PROJECTS, id: projectId }]));
    await dispatch(baseApi.util.invalidateTags([{ type: ServiceTypes.CCMS }]));
    await dispatch(baseApi.util.invalidateTags([{ type: 'Folders' }]));
    await dispatch(baseApi.util.invalidateTags([{ type: 'Types' }]));
  }, [dispatch, projectId]);

  const handleWorkbookUploadComplete = async () => {
    // refetch project data, so we can update stepper status indicators
    ProjectWorkbookUploadEventBus.publish('@clatter/PROJECT_WORKBOOK_UPLOAD_COMPLETED', undefined);
    await revalidateProjectState();
  };

  const handleUpdateProjectDisplayName = async (value: string) => {
    const trimmed = value?.trim();

    await updateProject({
      projectId: projectId,
      data: {
        displayName: trimmed,
      },
    }).unwrap();

    await revalidateProjectState();
  };
  //endregion

  useMountedEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const disableEdit = useDeepCompareMemo(
    () => !hasAccessByRole({ allowedRoles: [aclRolesMap.DATA_ADMIN, aclRolesMap.EDITOR], role: grants?.role }),
    [grants],
  );

  useEffect(() => {
    const unsubscribe = ProjectWorkbookUploadEventBus.subscribe(
      '@clatter/PROJECT_WORKBOOK_UPLOAD_COMPLETED',
      revalidateProjectState,
    );

    return () => {
      unsubscribe();
    };
  }, [revalidateProjectState]);

  return (
    <>
      <StyledHeaderContainer>
        <NgEditablePageTitle
          title={projectData?.DisplayName}
          isLoading={isLoading}
          onUpdate={handleUpdateProjectDisplayName}
          disableEdit={disableEdit}
        />

        <NgShareButton onClick={handleShare} />
      </StyledHeaderContainer>

      {(isAdmin || hasAccessByRole({ allowedRoles: [aclRolesMap.DATA_ADMIN], role: grants?.role })) &&
      !isMicrositeOnly &&
      !isPlacematGenerator ? (
        <NgGridLayout>
          <aside>
            <ProjectsLeftRail
              grants={grants}
              isLoading={isLoading}
              projectData={projectData}
              projectSideNavItems={projectSideNavItems}
              onWorkbookUploadComplete={handleWorkbookUploadComplete}
            />
          </aside>

          <div>{children}</div>
        </NgGridLayout>
      ) : (
        <div>{children}</div>
      )}

      {shareDialogState.opened && (
        <FolderManagerShareDialog
          opened={shareDialogState.opened}
          selectedRows={shareDialogState.selectedItems}
          onDismiss={() => closeShareDialog()}
          prefix="workspaces"
        />
      )}
    </>
  );
};
